/**
 * ArticleCard — blog listing card. `featured` lays it out side by side for
 * the newest article on the blog page.
 */
import { Link } from "@tanstack/react-router";
import { Image } from "@shopify/hydrogen-react";
import { BookOpen } from "lucide-react";
import { cn } from "@ui/lib/utils";
import {
  CardArrow,
  cardMedia,
  cardSurface,
} from "@modules/shared/Card/cardStyles";
import { formatArticleDate } from "./formatArticleDate";

interface ArticleCardArticle {
  id: string;
  handle: string;
  title: string;
  excerpt?: string | null;
  publishedAt: string;
  image?: {
    url: string;
    altText?: string | null;
  } | null;
  authorV2?: {
    name?: string | null;
  } | null;
  blog: {
    handle: string;
  };
}

interface ArticleCardProps {
  article: ArticleCardArticle;
  locale: string;
  featured?: boolean;
  headingLevel?: "h2" | "h3";
}

export function ArticleCard({
  article,
  locale,
  featured = false,
  headingLevel: Heading = "h2",
}: Readonly<ArticleCardProps>) {
  return (
    <Link
      to="/$locale/blog/$blogHandle/$articleHandle"
      params={{
        locale,
        blogHandle: article.blog.handle,
        articleHandle: article.handle,
      }}
      className={cn(
        cardSurface,
        featured && "md:grid md:grid-cols-2 md:items-center md:gap-10",
      )}
    >
      <div className={cn(cardMedia, featured ? "aspect-16/10" : "aspect-3/2")}>
        {article.image?.url ? (
          <Image
            src={article.image.url}
            alt={article.image.altText ?? article.title}
            className="size-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.02] motion-reduce:transition-none"
            loading={featured ? "eager" : "lazy"}
            sizes={
              featured
                ? "(min-width: 768px) 50vw, 100vw"
                : "(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
            }
          />
        ) : (
          <div className="flex size-full items-center justify-center bg-background-6">
            <BookOpen
              className="size-8 text-text-4"
              strokeWidth={1.5}
              aria-hidden
            />
          </div>
        )}
      </div>

      <div
        className={cn(
          "flex flex-1 flex-col px-2 pt-4 pb-2",
          featured && "md:p-6",
        )}
      >
        <p className="text-tny text-text-4 tabular-nums">
          <time dateTime={article.publishedAt}>
            {formatArticleDate(article.publishedAt, locale, "short")}
          </time>
          {article.authorV2?.name && (
            <>
              <span aria-hidden="true"> · </span>
              {article.authorV2.name}
            </>
          )}
        </p>

        <Heading
          className={cn(
            "mt-2 font-bold leading-snug text-balance text-text-1 transition-colors group-hover:text-primary",
            featured ? "text-xlarge max-mobile:text-large" : "text-medium",
          )}
        >
          {article.title}
        </Heading>

        {article.excerpt && (
          <p
            className={cn(
              "mt-3 text-pretty text-text-3",
              featured ? "line-clamp-4 text-base" : "line-clamp-3 text-small",
            )}
          >
            {article.excerpt}
          </p>
        )}
        <CardArrow className="mt-auto self-end" />
      </div>
    </Link>
  );
}
